import {
  FieldList,
  IFormGroupRules,
  IFormRules,
} from '@components/form/services';
import {
  CELL_RENDERER,
  DROPDOWN_MENU_POSITION,
  ListColumn,
  TOOLBAR_BUTTON_POSITION,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
  ITransferFormFieldPath,
  XuxGridAction,
  SORT_ORDERS,
  TRANSFER_TYPE,
  DEFAULT_GRID_HEIGHT,
} from '@shared/types';
import {
  DEFAULT_ITEM_LIST_TABLE_HEIGHT,
  MAX_NUMBER_FOR_TRANSFER_QUANTITY,
  MIN_NUMBER_FOR_TRANSFER_QUANTITY,
  WIDGET_FAMILY,
} from '@config/global-constants';
import {
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import {
  COMMON_ICONS_COLUMN,
  COMMON_WARN_ICON_COLUMN,
  COMMON_WARN_MESSAGE_CLASS,
} from '@app/shared/constants/common-warn-grid-setting';
import { TransferDestinationsContainerComponent } from '@containers/transfer/containers';
import { calculateUnitCost, getParQtyColumn } from '../transfer/transfer.util';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { getListGridRowCellClassForTransfer } from '@shared/helpers';
import { TransferDestinationsFormService } from './transfer-destinations.form.service';
import {
  DecimalNumber,
  getGroupStrCaseInsensitiveComparator,
  stringCaseInsensitiveComparator,
} from '@app/shared/common';
import { rx } from '@shared/rx';
import {
  CLEAR_QTY_REQUESTED,
  VISIBLE_TRANSFER_DESTINATION_GRID_ROW_COUNT,
} from '@shared/services/transfer/transfer-constants';
import { generateDecimalRegexp } from '@app/shared/constants';
import {
  BTN_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
  BTN_REPLENISH_QOH,
  BTN_REPLENISH_ZERO_QOH,
  GROUP_BTN_REPLENISH_BASE,
} from '../transfer-replenish/transfer-replenish.constants';
import { get, isEqual } from 'lodash';
import {
  getObservableValue,
  getQuickFilterConfigForScanning,
  getScanningHighlightRowClass,
} from '@app/shared/utils/common-utils';
import { I18n } from '@app/i18n/strings-keys';

export const createFormRules = (
  formService: TransferDestinationsFormService,
  editor: TransferDestinationsContainerComponent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  originFrom: string | undefined,
): IFormRules => {
  const formFieldPath: ITransferFormFieldPath = formService.formFieldPath;
  const { fields: totalFields } =
    formService.totalFormService.getTotalsFormRules(
      formService.formFieldPath,
      formService.isShownSubmittedColumns$,
    );

  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
      show: false,
    },
    item_list_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
    transfer_destinations_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
      show: false,
    },
  };

  const getIconsInfoColumns = [
    {
      ...COMMON_WARN_ICON_COLUMN,
      hide: true,
    },
    {
      ...COMMON_ICONS_COLUMN,
      cellRendererParams: formService.getIconsInfoRendererParams.bind(
        formService,
      )(formService.formFieldPath.siteIdTo),
    },
  ];

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: ['item_list_section'],
    },
    {
      id: 'item_list_section',
      fields: [
        'remove_message',
        'modified_message',
        'replenish_message',
        'threshold_message',
        'inventory_items',
        'virtual.totals',
      ],
    },
  ];
  const quickFilterOptions = getQuickFilterConfigForScanning(
    formService.isCategoryListView(),
  );

  return {
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        totals: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              item_count: { type: 'number' },
              total_cost: { type: 'number' },
            },
          },
        },
      },
    },
    fields: {
      ...sections,
      type: {
        omit: true,
      },
      transfer_type: {
        omit: true,
      },
      event_id: {
        omit: true,
      },
      site_id: {
        omit: true,
      },
      site_id_to: {
        omit: true,
      },
      transaction_date: {
        omit: true,
      },
      status: {
        omit: true,
      },
      replenish_message: {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        titleObs: formService.replenishMessageTitle$,
        hiddenObs: formService.replenishMessageHidden$,
        show: true,
        widgetData: {
          itemClass: COMMON_WARN_MESSAGE_CLASS,
          iconConfig: {
            icon: 'icon-warning',
            class: 'xvi-grid-warn-icon',
          },
        },
      },
      threshold_message: {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        titleObs: formService.thresholdInfoTitle$,
        hiddenObs: formService.hiddenThresholdInfo$,
        show: true,
        widgetData: {
          itemClass: COMMON_WARN_MESSAGE_CLASS,
          iconConfig: {
            icon: 'icon-warning',
            class: 'xvi-grid-dark-warning-icon',
          },
        },
      },
      inventory_items: {
        widget: WIDGET_COMPONENT.edit_grid_custom,
        title: I18n.Forms.GridForm.ItemList,
        class:
          'no-padding one-column-tree-grid transfer-items-grid navigate-grid',
        minItems: 0,
        widgetData: {
          showLoader: formService.isInventoryItemVenuesGridBusy$,
          gridHeight: DEFAULT_ITEM_LIST_TABLE_HEIGHT,
          gridMinHeight: DEFAULT_GRID_HEIGHT,
          useVisibleItemCountDescription: true,
          showPristineErrors: true,
          filterable: true,
          showSearch: true,
          multiselect: false,
          disableColumnSelector: true,
          orderControls: false,
          treeViewMode: formService.isCategoryListView(),
          defaultExpanded: false,
          ignoreItemValueOptimization: true,
          artificialInputCheckForSearchPanel: true,
          inlineEditOptions: {
            beforeActionMappers: [formService.getInlineChangeMapper()],
          },
          treeChildrenPropertyName: 'children',
          userGridOptions: {
            deltaRowDataMode: false,
            suppressMovableColumns: true,
            suppressHorizontalScroll: false,
            suppressClickEdit: true,
            suppressRowClickSelection: true,
            suppressRowTransform: true,
            defaultColDef: {
              editable: true,
            },
            rowClassRules: {
              ...getListGridRowCellClassForTransfer(),
              ...getScanningHighlightRowClass(formService.scanningService),
            },
          },
          buttons: [
            {
              caption: CLEAR_QTY_REQUESTED,
              class: 'btn btn-default mr-10',
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              click: formService.openClearQtyModal.bind(
                formService,
                I18n.Forms.TransferForm.QtyRequested,
                [
                  formFieldPath.initialCount,
                  'initialFactorAndCountValue.initial_count',
                ],
              ),
              disableWithGrid: true,
              disabledObs: formService.clearQtyRequestBtnDisable$.pipe(
                rx.map((value) => !editor.canEdit || value),
              ),
              hidden: () => formService.hideClearQtyRequestedBtn(),
            },
            {
              caption: I18n.Common.ViewSetting,
              class: 'btn-primary mr-10 ml-10',
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              click: formService.openViewSettingModal.bind(formService),
              disableWithGrid: false,
            },
            {
              ...GROUP_BTN_REPLENISH_BASE,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              class: 'btn-primary np-non-effect',
              dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
              disabledObs: formService.replenishButtonDisabled$.pipe(
                rx.map((value) => !editor.canEdit || value),
              ),
              childItems: [
                {
                  ...BTN_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                  hidden: () => {
                    const transferType = get(
                      getObservableValue(formService.routeState$),
                      ['storeForm', 'transfer_type'],
                    );

                    return !isEqual(
                      transferType,
                      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
                    );
                  },
                  click: formService.onClickReplenishHandler.bind(
                    formService,
                    BTN_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                  ),
                },
                {
                  ...BTN_REPLENISH_QOH,
                  click: formService.onClickReplenishHandler.bind(
                    formService,
                    BTN_REPLENISH_QOH,
                  ),
                },
                {
                  ...BTN_REPLENISH_ZERO_QOH,
                  click: formService.onClickReplenishHandler.bind(
                    formService,
                    BTN_REPLENISH_ZERO_QOH,
                  ),
                },
              ],
            },
          ],
          columns: [
            ...getIconsInfoColumns,
            {
              path: 'reporting_category_major.name',
              filterParams: {
                quickFilter: formService.isCategoryListView(),
              },
            },
            {
              path: 'reporting_category_minor.name',
              filterParams: {
                quickFilter: formService.isCategoryListView(),
              },
            },
            {
              path: 'is_chargeable',
              title: '',
              defaultView: true,
              sortable: !formService.isCategoryListView(),
              sort: SORT_ORDERS.DESC,
              sortingOrder: [SORT_ORDERS.DESC, SORT_ORDERS.ASC],
              headerComponentParams: {
                hintOptions: {
                  description:
                    I18n.InfoMessages.$IndicatesChargeableItemsBrackets,
                  placement: 'top',
                },
              },
              valueGetter: (params: ValueGetterParams): string => {
                if (!params.data.isLeaf) return null;

                return params.data[formService.formFieldPath.isChargeable]
                  ? '$'
                  : '';
              },
              minWidth: 60,
              maxWidth: 60,
            },
            {
              path: 'name',
              title: I18n.Forms.GridForm.ItemName,
              sortable: true,
              comparator: formService.isCategoryListView()
                ? getGroupStrCaseInsensitiveComparator()
                : stringCaseInsensitiveComparator,
              cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
              cellRendererParams: {
                // turn off the row count
                suppressCount: true,
              },
              defaultView: true,
              showRowGroup: true,
              filterParams: {
                quickFilter: true,
              },
              width: 220,
              minWidth: 220,
              ...quickFilterOptions,
            },
            {
              path: 'gl_account.name',
              title: I18n.Forms.GridForm.GLAccount,
              defaultView: true,
              filterParams: {
                quickFilter: true,
              },
              minWidth: 120,
            },
            {
              path: 'qoh_from',
              title: I18n.Forms.TransferForm.QOHFrom,
              defaultView: true,
              valueFormatter: formService.getQohValueGetter('qohFrom'),
              headerComponentParams: {
                hintOptions: {
                  description: formService.i18nService.instant(
                    I18n.InfoMessages.TransferQOHTooltips,
                    {
                      qohFromOrTo: formService.i18nService.translate(
                        I18n.Common.from,
                      ),
                    },
                  ),
                  placement: 'top',
                },
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: 'qoh_to',
              title: I18n.Forms.TransferForm.QOHTo,
              defaultView: true,
              valueFormatter: formService.getQohValueGetter('qohTo'),
              headerComponentParams: {
                hintOptions: {
                  description: formService.i18nService.instant(
                    I18n.InfoMessages.TransferQOHTooltips,
                    {
                      qohFromOrTo: formService.i18nService.translate(
                        I18n.Common.to,
                      ),
                    },
                  ),
                  placement: 'top',
                },
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: formService.formFieldPath.measureEntityId,
              title: I18n.Forms.TransferForm.TransferUnit,
              defaultView: true,
              editable: true,
              cellRendererSelector: (
                params: ICellRendererParams,
              ): ComponentSelectorResult | null =>
                params.data.isLeaf
                  ? { component: CELL_RENDERER.custom_select_renderer }
                  : null,
              cellRendererParams: (
                params: ICellRendererParams,
              ): ListColumn['cellRendererParams'] =>
                formService.transferFormService.getMeasureEntityIdCellRendererParams(
                  params,
                  editor,
                  VISIBLE_TRANSFER_DESTINATION_GRID_ROW_COUNT,
                ),
              valueGetter: (params: ValueGetterParams): number => {
                if (!params.data.isLeaf) return null;

                return params.data[formService.formFieldPath.measureEntityId];
              },
              cellClass:
                'cell-price ag-cell-editable-renderer cell-overflow-visible-always',
              minWidth: 120,
            },
            getParQtyColumn(formService),
            {
              path: formFieldPath.initialCount,
              title: I18n.Forms.TransferForm.QtyRequested,
              defaultView: true,
              editable: true,
              valueFormatter: (params: ValueFormatterParams): string =>
                params.data.isLeaf
                  ? formService.numberFormatService.getNumberFormatter(false)(
                      params,
                    )
                  : null,
              cellRendererSelector: (
                params: ICellRendererParams,
              ): ComponentSelectorResult | null => {
                return params.data.isLeaf
                  ? { component: CELL_RENDERER.float_input_renderer }
                  : null;
              },
              cellClassRules: formService.getQuantityCellClassRules(),
              cellRendererParams: (
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                params: ICellRendererParams,
              ): ListColumn['cellRendererParams'] => {
                return {
                  defaultValue: null,
                  placeholder: '0.00',
                  disabled: !editor.canEdit,
                  disableKeyUpFinishEditing: true,
                  min: MIN_NUMBER_FOR_TRANSFER_QUANTITY,
                  max: MAX_NUMBER_FOR_TRANSFER_QUANTITY,
                  acceptableInputRegex: generateDecimalRegexp(
                    formService.stateService.getNumberPrecision(),
                    false,
                  ),
                  usePreferenceDecimalPrecision: {
                    overrideMax: true,
                    overrideMin: true,
                  },
                  artificialInputCheck: true,
                };
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: 'item_cost',
              title: I18n.Forms.GridForm.UnitCost,
              defaultView: true,
              headerComponentParams: {
                hintOptions: {
                  description: I18n.InfoMessages.TransferUnitCostTooltips,
                  placement: 'top',
                },
              },
              valueFormatter: (params: ValueFormatterParams): string | null =>
                params.data.isLeaf
                  ? formService.numberFormatService.getNumberFormatter(true)(
                      params,
                    )
                  : null,
              valueGetter: (
                params: ValueGetterParams,
              ): DecimalNumber | null => {
                if (!params.data.isLeaf) return null;

                return calculateUnitCost(params);
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: 'total_cost',
              title: I18n.Forms.TransferForm.RequestCost,
              defaultView: true,
              valueFormatter: (params: ValueFormatterParams): string =>
                params.data.isLeaf
                  ? formService.numberFormatService.getNumberFormatter(true)(
                      params,
                    )
                  : null,
              valueGetter: (params: ValueGetterParams): DecimalNumber => {
                if (!params.data.isLeaf) return null;
                const { initial_count } = params.data;
                const unitCost = calculateUnitCost(params);
                const totalCost = new DecimalNumber(initial_count).mul(
                  unitCost,
                );

                return totalCost.isNaN ? null : totalCost;
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
          ],
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]: formService.getGridReadyHandler(),
            [XUX_GRID_ACTIONS.LIST_GROUP_COLLAPSED]: (
              action: XuxGridAction,
            ): void => action.payload?.gridApi.redrawRows(),
            [XUX_GRID_ACTIONS.LIST_GROUP_EXPANDED]: (
              action: XuxGridAction,
            ): void => action.payload?.gridApi.redrawRows(),
          },
        },
        suppressNoPermissionsDisable: true,
        validators: [formService.getInventoryItemVenuesRequireValidator()],
      },
      total_cost: {
        show: false,
        required: false,
      },
      ...totalFields,
    },
    groups: [...panelGroups],
  };
};
